import './styles/DeveloperLogin.scss';

import { useQuery } from '@apollo/client';
import { ArrowBack, PersonAddAlt1 } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import {
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Divider,
  Grid,
  Grid2,
  List,
  ListItem,
  Paper,
  Tooltip
} from '@mui/material';
import { Course, Faculty } from '@thro-types/core';
import { ShibUser } from '@thro-types/users';

import { Gateway } from 'index';
import { Base64 } from 'js-base64';
import { JsonEditor as Editor } from 'jsoneditor-react18';
import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDarkReasonable } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import USER_QUERY from 'gql/queries/currentUser.gql';
import USERS_QUERY from 'gql/queries/getTestUsers.gql';

import { TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import GQLGuard from 'app/components/GQLGuard';
import InfoBox from 'app/components/InfoBox';
import { SubtleActionButton } from 'app/components/SubtleActionButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { entitlements } from 'utils/entitlements';

import getSampleData from '../sampleData';

interface Props {
  closeHandler: Function;
}

export function DeveloperLoginModal(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [openUser, setOpenUser] = useState<
    (ShibUser & { courses: Course[]; faculties: Faculty[] }) | undefined
  >(undefined);

  const [submitting, setSubmitting] = useState(false);
  const [userInput, setUserInput] = useState(getSampleData());
  const keywordState = useState();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const [view, setView] = useState<'general' | 'select' | 'create'>('general');

  const usersQuery = useQuery<{ getTestUsers: any }>(USERS_QUERY, {
    variables: { offset: 0, limit: paginationModel.pageSize }
  });
  const { refetch: getUser } = useQuery<any, any>(USER_QUERY, {
    skip: true,
    context: { clientName: 'federation' }
  });

  const { refetch } = usersQuery;

  const handlePagination = useCallback(
    (event, page: number) => {
      const offset = page * paginationModel.pageSize;
      refetch({ offset: offset });
    },
    [paginationModel.pageSize, refetch]
  );

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const createTestUser = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await Gateway.get('/Shibboleth.sso/login?local=true', {
        headers: userInput ? { user: Base64.encode(JSON.stringify(userInput)) } : {}
      });
      localStorage.setItem('jwt-token', data['access_token']);
      localStorage.setItem('refresh-token', data['refresh_token']);

      await getUser();
      //! navigate not working -> should be used in useEffect, but maybe still effects internal states -> navigate + href
      if (state) {
        navigate(state?.from?.pathname, { replace: true });
        window.location.href = state?.from?.pathname;
      } else {
        const locationAfterLogin = localStorage.getItem('redirect-after-login');
        if (locationAfterLogin) {
          localStorage.removeItem('redirect-after-login');
          navigate(locationAfterLogin, { replace: true });
          window.location.href = locationAfterLogin;
        } else {
          navigate('/', { replace: true });
          window.location.href = '/';
        }
      }
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.login.alerts.registerFailed())));
    } finally {
      setSubmitting(false);
      props.closeHandler();
    }
  };

  const handleEmailLogin = async email => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await Gateway.get(`/Shibboleth.sso/login?local=true&email=true`, {
        headers: email ? { email: JSON.stringify(email) } : {}
      });
      localStorage.setItem('jwt-token', data['access_token']);
      localStorage.setItem('refresh-token', data['refresh_token']);
      await getUser();
      //! navigate not working -> should be used in useEffect, but maybe still effects internal states -> navigate + href
      if (state) {
        navigate(state?.from?.pathname, { replace: true });
        window.location.href = state?.from?.pathname;
      } else {
        const locationAfterLogin = localStorage.getItem('redirect-after-login');
        if (locationAfterLogin) {
          localStorage.removeItem('redirect-after-login');
          navigate(locationAfterLogin, { replace: true });
          window.location.href = locationAfterLogin;
        } else {
          navigate('/', { replace: true });
          window.location.href = '/';
        }
      }
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.login.alerts.loginFailed())));
    } finally {
      setSubmitting(false);
      props.closeHandler();
    }
  };

  const filters: any[] | undefined = [
    {
      field: 'keyword',
      label: t(translations.pages.internationalOffice.users.search()),
      stateHandler: keywordState
    }
  ];
  const columns = useMemo((): TypedGridColumns<ShibUser> => {
    return [
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.common.email()),
        field: 'email',
        renderCell: params => (
          <Tooltip title={params.row.email}>
            <span>{params.row.email}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.common.firstName()),
        field: 'firstName',
        renderCell: params => (
          <Tooltip title={params.row.firstName}>
            <span>{params.row.firstName}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.common.lastName()),
        field: 'lastName',
        renderCell: params => (
          <Tooltip title={params.row.lastName}>
            <span>{params.row.lastName}</span>
          </Tooltip>
        )
      }
    ];
  }, [t]);

  const editorProps = {
    style: { height: '650px' }
  };

  return (
    <Grid2 container justifyContent={'center'} alignItems={'center'} minHeight={'8em'}>
      <GQLGuard
        query={[usersQuery]}
        onError={error => {
          dispatch(AlertActions.error(`could not load test users`));
          return null;
        }}
      >
        {([usersData]) => {
          return (
            <Grid container className="developerLogin">
              <Dialog open={!!openUser} onClose={() => setOpenUser(undefined)}>
                <DialogTitle>
                  {openUser?.lastName}, {openUser?.firstName}
                </DialogTitle>
                <Grid container style={{ padding: '2em', overflow: 'auto' }}>
                  <Grid item xs={12}>
                    <Button
                      className="developerLogin__form__login__button"
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={submitting}
                      onClick={() => handleEmailLogin(openUser?.email)}
                      style={{ marginBottom: '2em' }}
                    >
                      {t(translations.pages.login.titleEmail())}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    UID: {openUser?.uid}
                  </Grid>
                  <Grid item xs={12}>
                    Email: {openUser?.email}
                  </Grid>
                  <Grid item xs={12}>
                    Semester: {openUser?.semester}
                  </Grid>
                  <Grid item xs={12}>
                    Degree: {openUser?.degree}
                  </Grid>
                  <Divider />
                  <Grid container className="shibUserDetail__info__headline">
                    {t(translations.pages.shibUserList.communities())}:
                  </Grid>
                  <Grid container className="shibUserDetail__info__communities">
                    {openUser?.communities?.map((community, i) => (
                      <Chip key={i} label={community} />
                    ))}
                  </Grid>
                  <Grid container className="shibUserDetail__info__headline">
                    Faculties
                  </Grid>
                  <Grid container className="shibUserDetail__info__roles">
                    {openUser?.faculties?.map((faculty, i) => (
                      <Chip key={i} label={faculty.shorthand} />
                    ))}
                  </Grid>
                  <Grid container className="shibUserDetail__info__headline">
                    Courses
                  </Grid>
                  <Grid container className="shibUserDetail__info__roles">
                    {openUser?.courses?.map((course, i) => (
                      <Chip key={i} label={course.shorthand} />
                    ))}
                  </Grid>
                  <Grid container className="shibUserDetail__info__headline">
                    {t(translations.pages.shibUserList.entitlements())}:
                  </Grid>
                  <Grid container className="shibUserDetail__info__entitlements">
                    {openUser?.entitlements?.map((entitlement, i) => (
                      <Chip key={i} label={entitlement} />
                    ))}
                  </Grid>
                  <Grid item xs={12}>
                    <SyntaxHighlighter
                      language="json"
                      style={atomOneDarkReasonable}
                      customStyle={{ width: '100%' }}
                    >
                      {JSON.stringify(JSON.parse(openUser?.meta ?? '{}'), null, 2)}
                    </SyntaxHighlighter>
                  </Grid>
                </Grid>
              </Dialog>
              {view === 'general' && (
                <Grid
                  container
                  justifyContent={'center'}
                  flexDirection={'column'}
                  alignItems={'center'}
                  gap={4}
                >
                  <Grid className="developerLogin__headline" item xs={12} padding={1}>
                    {t(
                      translations.pages.login.loginSelection.developerLogin.title()
                    ).toUpperCase()}
                  </Grid>
                  <Grid
                    item
                    component={Paper}
                    onClick={() => setView('select')}
                    style={isMobile ? { width: '100%' } : { width: '400px' }}
                    className="developerLogin__form__choose"
                  >
                    <Grid className="developerLogin__icon" item xs={12}>
                      <PublishedWithChangesIcon className="developerLogin__icon" />
                    </Grid>
                    <Grid className="developerLogin__subheadline" item xs={12} padding={2}>
                      Nutzer auswählen
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    component={Paper}
                    onClick={() => setView('create')}
                    style={isMobile ? { width: '100%' } : { width: '400px' }}
                    className="developerLogin__form__choose"
                  >
                    <Grid className="developerLogin__icon" item xs={12}>
                      <AddCircleOutlineIcon className="developerLogin__icon" />
                    </Grid>
                    <Grid className="developerLogin__subheadline" item xs={12} padding={2}>
                      Neuen Nutzer erstellen
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {view === 'select' && (
                <Grid container direction="row">
                  <Grid item xs={1} textAlign="start" justifyContent={'center'} marginTop="1em">
                    <SubtleActionButton
                      type="neutral"
                      icon={ArrowBack}
                      onClick={() => setView('general')}
                    />
                  </Grid>
                  <Grid
                    container
                    item
                    xs={10}
                    flexGrow={1}
                    style={{ height: '700px', marginTop: '1em' }}
                    component={Paper}
                  >
                    <DataGrid
                      slotProps={{
                        toolbar: {
                          filters: {
                            filterConfig: filters,
                            onFilterChange: refetch
                          }
                        }
                      }}
                      disableColumnFilter
                      disableRowSelectionOnClick
                      columns={columns.map(column => ({
                        ...column,
                        sortable: false
                      }))}
                      rows={usersData.getTestUsers}
                      paginationMode="server"
                      // onPageSizeChange={handlePageSizeChange}
                      // pageSize={pageSize}
                      rowCount={usersData.getTestUsersCount}
                      onRowClick={params => setOpenUser(params.row)}
                      paginationModel={{
                        page: paginationModel.page,
                        pageSize: paginationModel.pageSize
                      }}
                      onPaginationModelChange={pagModel => {
                        handlePaginationChange(pagModel);
                        handlePagination(null, pagModel.page);
                      }}
                      sx={{ '.MuiDataGrid-row:hover': { cursor: 'pointer' } }}
                    />
                  </Grid>
                </Grid>
              )}
              {view === 'create' && (
                <Grid container direction="row">
                  <Grid item xs={1} textAlign="start" justifyContent={'center'} marginTop="1em">
                    <SubtleActionButton
                      type="neutral"
                      icon={ArrowBack}
                      onClick={() => setView('general')}
                    />
                  </Grid>
                  <Grid
                    container
                    item
                    xs={11}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <Grid item xs={12} paddingTop={2}>
                      <InfoBox title={t(translations.common.general())} type="warning">
                        Beachte beim Erstellen die Eingabe der Daten <b>genausten</b>!
                        <br />
                        <br />
                        Ein Student benötigt bei seiner "relationship" immer <b>
                          Semester
                        </b> und <b>Studienart</b>.
                        <br />
                        <br />
                        Ein Mitarbeiter kann bei seiner relationship hingegen <b>
                          Departments
                        </b>{' '}
                        besitzen.
                      </InfoBox>
                    </Grid>
                    <Grid className="devLogin_create_New_user" item xs={12} paddingTop={1}>
                      <Editor
                        schema={undefined}
                        value={userInput}
                        onChange={setUserInput}
                        mode={'code'}
                        theme="ace/theme/tomorrow_night_blue"
                        htmlElementProps={editorProps}
                      />
                      <Grid paddingTop={2}>
                        <Button
                          startIcon={<PersonAddAlt1 fontSize="large" />}
                          className="create-and-login-user"
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={submitting}
                          onClick={createTestUser}
                        >
                          {t(translations.pages.login.titleUserCreate())}
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid className="devLogin_create_New_user" container marginTop={1}>
                      <Grid item xs={4} padding={0.5}>
                        <InfoBox title={`eduPersonAffiliation`} type="info" expandable collapsed>
                          Affilitaion sind Rollen bzw. Gruppen, die der Nutzer angehört.
                          <List disablePadding dense>
                            <ListItem>- staff</ListItem>
                            <ListItem>- faculty</ListItem>
                            <ListItem>- member</ListItem>
                            <ListItem>- student</ListItem>
                          </List>
                        </InfoBox>
                      </Grid>
                      <Grid item xs={4} padding={0.5}>
                        <InfoBox title={`eduPersonEntitlements`} type="info" expandable collapsed>
                          Entitlements des Nutzers beschreiben genaue Rechte und Zugehörigkeiten.
                        </InfoBox>
                      </Grid>
                      <Grid item xs={4} padding={0.5}>
                        <InfoBox title={`relationship`} type="info" expandable collapsed>
                          Die Relationship definiert die Zugehörigkeit zu einer Fakultät und
                          beschreibt diese näher.
                        </InfoBox>
                      </Grid>
                      <Grid item xs={12} marginTop={1}>
                        <InfoBox title={`Entitlements`} type="info" expandable collapsed>
                          <List disablePadding dense>
                            {entitlements.map(entitlement => (
                              <ListItem key={entitlement}>- {entitlement}</ListItem>
                            ))}
                          </List>
                        </InfoBox>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          );
        }}
      </GQLGuard>
    </Grid2>
  );
}
